import { ChangeEvent, useEffect, useMemo, useState } from "react";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { DEFAULT_DCUI_CONTENT, DEFAULT_DCUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { LOGIN_HINT_PROPERTY_TYPE_IDS, OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID } from "@constants/oidc-login-hint";
import { PIN_CODE_PROPERTY_TYPE_IDS } from "@constants/pin-code-constants";
import { POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID } from "@constants/prefilled-credential";
import { WEBHOOK_CONFIGURATION_PROPERTY_TYPE_IDS } from "@constants/webhook-constants";
import {
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID,
    DCUI_VERSION_PROPERTY_TYPE_ID,
    REDIRECT_URLS_PROPERTY_TYPE_ID,
    TMX_EVENT_TYPE_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ServiceEndpointTypes } from "@enums/service-endpoint-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationProperty
} from "@idw-types/data/rp-service-configuration";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { hasEmailPinServiceOffering } from "@utils/pin-code/pin-code-utils";
import { setValueAndAddedToServiceForProperty } from "@utils/login-hint/login-hint-utils";
import { isNullOrUndefined } from "@utils/object-util";
import { getTmxEventType, hasTmxServiceOffering } from "@utils/tmx-event-type/tmx-event-type-util";
import { getSessionTokenInfo, initializeSessionTimeoutProperties } from "@utils/custom-session-timeout/custom-session-timeout-util";
import { CustomSessionTimeoutContainer } from "@containers/common/properties-sections/custom-session-timeout-container/custom-session-timeout-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { IdwFormLabel } from "@containers/common/idw-form-label/idw-form-label";
import { AddRpServiceConfigurationPropertyModal } from "@containers/modals/add-rp-service-configuration-property-modal/add-rp-service-configuration-property-modal";
import { RpServiceConfigurationPropertiesContainer } from "@containers/common/rp-service-configuration-properties-container/rp-service-configuration-properties-container";
import { PinCodeContainer } from "@containers/common/properties-sections/pin-code-container/pin-code-container";
import { PrefilledCredentialContainer } from "@containers/common/properties-sections/prefilled-credential-container/prefilled-credential-container";
import { TmxEventTypeContainer } from "@containers/common/properties-sections/tmx-event-type-container/tmx-event-type-container";
import { VerificationServiceWebhookConfigurationContainer } from "./verification-service-webhook-configuration-container/verification-service-webhook-configuration-container";
import { VerificationServiceLoginHintContainer } from "./verification-service-login-hint-container/verification-service-login-hint-container";
import commonStyles from "../../verification-service-details-component.less";
import styles from "./advanced-tab-container.less";
import { GatewayPropertiesTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-gateway-tab/gateway-properties-tab-container/gateway-properties-tab-container";
import { VerificationServiceIncodeSmsContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-general-tab/advanced-tab-container/verification-service-incode-sms-container/verification-service-incode-sms-container";
import { INCODE_SMS_USE_IDW_TO_SEND_PROPERTY_ID } from "@constants/incode-sms-constants";

type AdvancedTabContainerProps = {
    isDisabled: boolean;
    isAdminUser: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    validationErrors: GeneralAdvancedValidationErrors;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    selectedAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    showPinCodeSection: boolean;
    showIncodeFlowTabs: boolean;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const AdvancedTabContainer = (props: AdvancedTabContainerProps) => {
    const {
        isDisabled,
        isAdminUser,
        rpServiceConfigurationState,
        validationErrors,
        axnDocumentationLinks,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        selectedAttributes,
        showPinCodeSection,
        showIncodeFlowTabs,
        setRpServiceConfigurationState
    } = props;

    const modalContext = useModalContext();
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const handleErrorUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            errorURL: event.target.value
        });
    };

    const handleEndpointTypeChange = (newValue: string) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            endpointTypeID: newValue
        });
    };

    const generalPropertyIds = useMemo(() => {
        return rpServiceConfigurationState.properties.filter(property =>
            property.category === "general" &&
            ![
                ...LOGIN_HINT_PROPERTY_TYPE_IDS,
                ...PIN_CODE_PROPERTY_TYPE_IDS,
                ...WEBHOOK_CONFIGURATION_PROPERTY_TYPE_IDS,
                POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID,
                TMX_EVENT_TYPE_PROPERTY_TYPE_ID,
                INCODE_SMS_USE_IDW_TO_SEND_PROPERTY_ID,
                REDIRECT_URLS_PROPERTY_TYPE_ID,
                OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID
            ].includes(property.propertyTypeId)
        ).map(property => property.propertyTypeId);
    }, []);

    const permissionBasedIsDisabled = isDisabled || !rpServiceConfigurationState.permissions.canModifyRPServiceProperties;
    const unselectedGeneralProperties = rpServiceConfigurationState.properties.filter(property => generalPropertyIds.includes(property.propertyTypeId) && !property.addedToService);
    const selectedGeneralProperties = rpServiceConfigurationState.properties.filter(property => generalPropertyIds.includes(property.propertyTypeId) && property.addedToService);

    const showSuppressEmailPinInput = useMemo(() => {
        return hasEmailPinServiceOffering(rpServiceConfigurationState.serviceOfferings);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const showTmxEventTypeWidget = useMemo(() => {
        return hasTmxServiceOffering(rpServiceConfigurationState.serviceOfferings);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const addRpServiceConfigurationProperty = (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            // if we find the property, update it
            if (property.propertyTypeId === rpServiceConfigurationProperty.propertyTypeId) {
                return rpServiceConfigurationProperty;
            } else if (rpServiceConfigurationProperty.propertyTypeId === DCUI_VERSION_PROPERTY_TYPE_ID) {
                // if the new property is dcuiVersion, we need to add the DCUI theming and content properties
                if (property.propertyTypeId === DCUI_CONTENT_PROPERTY_TYPE_ID) {
                    return {
                        ...property,
                        addedToService: true,
                        //TODO - remove english constraint after migration
                        value: JSON.stringify(DEFAULT_DCUI_CONTENT[LocaleTypes.ENGLISH])
                    };
                } else if (property.propertyTypeId === DCUI_THEME_PROPERTY_TYPE_ID) {
                    return {
                        ...property,
                        addedToService: true,
                        value: JSON.stringify(DEFAULT_DCUI_THEME)
                    };
                }
            }
            return property;
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const handleUpdateAttributes = (attributes: Array<FormattedRpServiceConfigurationAttribute>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            attributes
        });
    };

    const updateProperties = (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            const foundIndex = properties.findIndex(passedProperty => passedProperty.propertyTypeId === property.propertyTypeId);
            if (foundIndex > -1) {
                return setValueAndAddedToServiceForProperty(property, properties[foundIndex].newValue, properties[foundIndex].added);
            }

            return property;
        });

        setRpServiceConfigurationState({ ...rpServiceConfigurationState, properties: newProperties });
    };

    const onClickAddProperty = () => {
        const renderAddPropertyModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationPropertyModal
                    title={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.buttons.addProperty.label}
                    rpServiceConfigurationProperties={unselectedGeneralProperties}
                    addRpServiceConfigurationProperty={addRpServiceConfigurationProperty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddPropertyModal,
            modalType: ModalTypes.AddProperty
        });
    };

    const generalPropertiesHeaderButton = (
        (!permissionBasedIsDisabled) ? (
            <Button id="add-general-property" onClick={onClickAddProperty} disabled={unselectedGeneralProperties.length === 0}>
                {resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.buttons.addProperty.label}
            </Button>
        ) : null
    );

    const { asiTimeoutPropertyValue: inheritedAsiTimeoutPropertyValue, customTokenTimeoutPropertyValue: inheritedCustomTokenTimeoutPropertyValue } = useMemo(() => {
        return getSessionTokenInfo([], rpServiceConfigurationState.appliedProfiles);
    }, [rpServiceConfigurationState.appliedProfiles]);


    const { asiTimeoutPropertyValue, customTokenTimeoutPropertyValue } = useMemo(() => {
        return getSessionTokenInfo(rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles);
    }, [rpServiceConfigurationState.appliedProfiles, rpServiceConfigurationState.properties]);

    const inheritedTMXEventTypePropertyValue = useMemo(() => {
        return getTmxEventType([], rpServiceConfigurationState.appliedProfiles);
    }, [rpServiceConfigurationState.appliedProfiles]);

    const tmxEventTypePropertyValue = useMemo(() => {
        return getTmxEventType(rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles);
    }, [rpServiceConfigurationState.appliedProfiles, rpServiceConfigurationState.properties]);

    const showSessionTimeoutWidget = useMemo(() => {
        return !isNullOrUndefined(asiTimeoutPropertyValue) || !isNullOrUndefined(customTokenTimeoutPropertyValue);
    }, [asiTimeoutPropertyValue, customTokenTimeoutPropertyValue]);

    useEffect(() => {
        // if not part of a workflow or no workflow properties have been set,
        // initialize session timeout properties
        if (
            (!isDisabled && rpServiceConfigurationState.permissions.canModifyNonRestrictedProperties) &&
            (
                !rpServiceConfigurationState.workflowID ||
                (
                    isNullOrUndefined(inheritedAsiTimeoutPropertyValue) &&
                    isNullOrUndefined(inheritedCustomTokenTimeoutPropertyValue)
                )
            )
        ) {
            initializeSessionTimeoutProperties(
                asiTimeoutPropertyValue,
                customTokenTimeoutPropertyValue,
                updateProperties
            );
        }
    }, [isDisabled]);



    return (
        <div className={styles.advancedTabContainer}>
            <div>
                <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.title} />
                <ContentPageBody isMaxWidth={true}>
                    <InputFormGroup
                        controlId={"verification-service-details-error-url"}
                        automationId={"verification-service-details-error-url"}
                        label={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.inputs.errorUrl.label}
                        ariaLabel={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.inputs.errorUrl.label}
                        value={rpServiceConfigurationState.errorURL || ""}
                        disabled={isDisabled}
                        isRequired={false}
                        labelWidth={labelWidth}
                        type={"url"}
                        placeholder={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.inputs.errorUrl.placeholder}
                        onChange={handleErrorUrlChange}
                    />
                    <div className={styles.endpointTypeContainer}>
                        <div className={styles.endpointTypeButtonGroupFormGroup}>
                            <IdwFormLabel isRequired={false} label={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.inputs.endpointType.label} labelWidth={labelWidth} />
                            <ButtonGroup className={styles.endpointTypeButtonGroup}>
                                <Button
                                    id="set-endpoint-type-id-1"
                                    disabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyEndpointType}
                                    onClick={() => handleEndpointTypeChange(ServiceEndpointTypes.LEGACY)}
                                    variant={rpServiceConfigurationState.endpointTypeID === ServiceEndpointTypes.LEGACY ? "primary" : "outline-primary"}
                                >
                                    {"1"}
                                </Button>
                                <Button
                                    id="set-endpoint-type-id-2"
                                    disabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyEndpointType}
                                    onClick={() => handleEndpointTypeChange(ServiceEndpointTypes.OIDC)}
                                    variant={rpServiceConfigurationState.endpointTypeID === ServiceEndpointTypes.OIDC ? "primary" : "outline-primary"}
                                >
                                    {"2"}
                                </Button>
                                <Button
                                    id="set-endpoint-type-id-3"
                                    disabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyEndpointType}
                                    onClick={() => handleEndpointTypeChange(ServiceEndpointTypes.OIDC_FLAT)}
                                    variant={rpServiceConfigurationState.endpointTypeID === ServiceEndpointTypes.OIDC_FLAT ? "primary" : "outline-primary"}
                                >
                                    {"3"}
                                </Button>
                            </ButtonGroup>
                        </div>
                        <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.inputs.endpointType.warning}</Alert>
                    </div>
                </ContentPageBody>
            </div>

            {rpServiceConfigurationState.permissions.canViewNonRestrictedProperties && (
                <>
                    <VerificationServiceLoginHintContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors}
                        documentationLinks={axnDocumentationLinks}
                        rpServiceConfiguration={rpServiceConfigurationState}
                        updateProperties={updateProperties}
                    />

                   {showIncodeFlowTabs && <VerificationServiceIncodeSmsContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors}
                        documentationLinks={axnDocumentationLinks}
                        rpServiceConfiguration={rpServiceConfigurationState}
                        updateProperties={updateProperties} />}

                    {showSessionTimeoutWidget &&
                        <CustomSessionTimeoutContainer
                            isDisabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyNonRestrictedProperties}
                            validationErrors={validationErrors}
                            pageLevel={PageLevelTypes.APIKEY}
                            inheritedAsiTimeoutPropertyValue={inheritedAsiTimeoutPropertyValue}
                            inheritedCustomTokenTimeoutPropertyValue={inheritedCustomTokenTimeoutPropertyValue}
                            documentationLinks={axnDocumentationLinks}
                            asiTimeoutPropertyValue={asiTimeoutPropertyValue}
                            customTokenTimeoutPropertyValue={customTokenTimeoutPropertyValue}
                            updateProperties={updateProperties}
                            workflowId={rpServiceConfigurationState.workflowID}
                        />
                    }

                    {showPinCodeSection && (
                        <PinCodeContainer
                            isDisabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyNonRestrictedProperties}
                            validationErrors={validationErrors}
                            showSuppressEmailPinInput={showSuppressEmailPinInput}
                            documentationLinks={axnDocumentationLinks}
                            properties={rpServiceConfigurationState.properties}
                            appliedProfiles={rpServiceConfigurationState.appliedProfiles}
                            updateProperties={updateProperties}
                        />
                    )}

                    <PrefilledCredentialContainer
                        isDisabled={isDisabled || !rpServiceConfigurationState.permissions.canModifyNonRestrictedProperties || !rpServiceConfigurationState.permissions.canModifyAttributeProperties}
                        validationErrors={validationErrors}
                        documentationLinks={axnDocumentationLinks}
                        updateAttributes={handleUpdateAttributes}
                        updateProperties={updateProperties}
                        properties={rpServiceConfigurationState.properties}
                        primaryServiceFormattedAttributes={rpServiceConfigurationState.attributes}
                    />

                    <VerificationServiceWebhookConfigurationContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors}
                        apiKey={rpServiceConfigurationState.apiKey}
                        documentationLinks={axnDocumentationLinks}
                        rpServiceConfiguration={rpServiceConfigurationState}
                        updateProperties={updateProperties}
                    />

                    {showTmxEventTypeWidget && (
                        <TmxEventTypeContainer
                            isDisabled={isDisabled || !isAdminUser}
                            inheritedTmxEventType={inheritedTMXEventTypePropertyValue}
                            tmxEventType={tmxEventTypePropertyValue}
                            pageLevel={PageLevelTypes.APIKEY}
                            workflowId={rpServiceConfigurationState.workflowID}
                            updateProperties={updateProperties}
                        />
                    )}
                </>
            )}
            {rpServiceConfigurationState.permissions.canViewRPServiceProperties &&
                (<div>
                    <ContentPageHeader
                        title={resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.title}
                        headerButton={generalPropertiesHeaderButton}
                    />
                    <ContentPageBody isMaxWidth={true}>
                        <span className={commonStyles.description}>
                            {resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.description}
                        </span>
                        <div>
                            {unselectedGeneralProperties.length === 0 && (
                                <Alert variant={"info"}>
                                    {resources.pages.verificationServiceDetails.tabs.common.alerts.allPropertiesMessage(
                                        resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.title
                                    )}
                                </Alert>
                            )}
                            {selectedGeneralProperties.length === 0 && (
                                <Alert variant={"info"}>
                                    {resources.pages.verificationServiceDetails.tabs.common.alerts.noPropertiesMessage(
                                        resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.generalProperties.title,
                                        permissionBasedIsDisabled
                                    )}
                                </Alert>
                            )}
                            {selectedGeneralProperties.length > 0 && (
                                <div className={styles.propertiesContainer}>
                                    <RpServiceConfigurationPropertiesContainer
                                        isDisabled={permissionBasedIsDisabled}
                                        selectedRpServiceConfigurationProperties={selectedGeneralProperties}
                                        rpServiceConfigurationState={rpServiceConfigurationState}
                                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                                    />
                                </div>
                            )}
                        </div>
                    </ContentPageBody>

                    <GatewayPropertiesTabContainer
                        isDisabled={isDisabled}
                        selectedAttributes={selectedAttributes}
                        selectedLanguage={selectedLanguage}
                        availableLanguages={availableLanguages}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </div>
                )}
        </div>
    );
};